/**
 * The predefined UIs. Each is data: copy one, change it, give it a new id — or build it live in the
 * layout editor (?dev=1) and export it. A domain pack can add its own (DomainPack.presets).
 *
 * Canvas 1080×1920. Reach: on a 1.9 m totem the lower half is where a hand is comfortable, so the
 * things you touch (content, main bar) sit low and the face (avatar, chat) sits at eye height.
 */
import { parsePreset, type Preset } from './model';

/**
 * Section sizes measured on the old kiosk (backend/frontend, 1080×1920): progress 58, chat + avatar 968,
 * content 670, buttons 168, help line 56. The text box sits at the bottom of the avatar half.
 */
export const SECTION = { progress: 58, stage: 968, content: 670, mainBar: 168, status: 56 } as const;

/** The voice-first totem: chat and avatar side by side at eye height, the task below. */
export const KIOSK: Preset = {
  id: 'kiosk',
  label: 'preset.kiosk',
  icon: 'assistant',
  scene: 'stage',
  transition: 'morph',
  root: {
    kind: 'split', id: 'root', direction: 'column', children: [
      { kind: 'slot', module: 'progress', size: { px: SECTION.progress } },
      { kind: 'split', id: 'stage', direction: 'row', size: { fr: 1 }, children: [
        // a column of its own, as in the text preset: a mode can put the text box under the chat
        { kind: 'split', id: 'conversation', direction: 'column', children: [{ kind: 'slot', module: 'chat' }] },
        { kind: 'split', id: 'avatar-side', direction: 'column', children: [
          { kind: 'slot', module: 'avatar-window', size: { fr: 1 } },
          { kind: 'slot', module: 'composer', size: { px: 68 } },
        ] },
      ] },
      { kind: 'slot', module: 'content', size: { px: SECTION.content } },
      { kind: 'slot', module: 'main-bar', size: { px: SECTION.mainBar } },
      { kind: 'slot', module: 'status', size: { px: SECTION.status }, pin: 'bottom' },
    ],
  },
};

/** Text mode: no avatar, the conversation takes the whole width; same sections as the kiosk. */
export const TEXT: Preset = {
  id: 'text',
  label: 'preset.text',
  icon: 'text',
  scene: 'text',
  transition: 'morph',
  // one centred column, a little larger: cleaner without the avatar beside it
  appearance: { chatAlign: 'center', chatScale: 1.15, chatWidth: 820 },
  root: {
    kind: 'split', id: 'root', direction: 'column', children: [
      { kind: 'slot', module: 'progress', size: { px: SECTION.progress } },
      { kind: 'split', id: 'conversation', direction: 'column', size: { fr: 1 }, children: [
        { kind: 'slot', module: 'chat', size: { fr: 1 } },
        { kind: 'slot', module: 'composer', size: { px: 84 } },
      ] },
      { kind: 'slot', module: 'content', size: { px: SECTION.content } },
      { kind: 'slot', module: 'main-bar', size: { px: SECTION.mainBar } },
      { kind: 'slot', module: 'status', size: { px: SECTION.status }, pin: 'bottom' },
    ],
  },
};

/** The main bar on top, under the progress: the chat and the avatar move down by its height. */
export const MAIN_BAR_TOP: Preset = {
  ...KIOSK,
  id: 'main-bar-top',
  label: 'preset.main_bar_top',
  icon: 'panel-top',
  stageOffsetY: SECTION.mainBar,
  root: {
    ...KIOSK.root,
    children: [
      KIOSK.root.children[0]!, // progress
      { kind: 'slot', module: 'main-bar', size: { px: SECTION.mainBar } },
      ...KIOSK.root.children.filter((c) => c.kind === 'split' || ['content', 'status'].includes(c.module)),
    ],
  },
};

/**
 * The task first: the content high, the kiosk's two columns under it (chat on the left, the avatar and
 * the text box on the right). The stage moves down with them by the content's height, and 8 px more: the avatar's
 * lower edge goes behind the main bar, not into the room the text box leaves above it.
 */
export const CONTENT_FIRST: Preset = {
  ...KIOSK,
  id: 'content-first',
  label: 'preset.content_first',
  icon: 'app-window',
  stageOffsetY: SECTION.content + 8,
  root: {
    ...KIOSK.root,
    children: [
      KIOSK.root.children[0]!, // progress
      ...KIOSK.root.children.filter((c) => c.kind === 'slot' && c.module === 'content'),
      ...KIOSK.root.children.filter((c) => c.kind === 'split'), // the stage: chat | avatar + text box
      ...KIOSK.root.children.filter((c) => c.kind === 'slot' && ['main-bar', 'status'].includes(c.module)),
    ],
  },
};

/**
 * Essenziale: one column on a plain background. A small avatar at the top, the assistant's words in
 * the middle (lyrics), the task card, pill buttons, a quiet footer. The Essenziale theme's preset.
 */
export const ESSENZIALE: Preset = {
  id: 'essenziale',
  label: 'preset.essenziale',
  icon: 'sparkles',
  scene: 'plain',
  transition: 'morph',
  appearance: { chatAlign: 'center' },
  root: {
    kind: 'split', id: 'root', direction: 'column', children: [
      // the orb high, so a long answer has the room under it (21/10)
      { kind: 'slot', module: 'progress', size: { px: 88 } },
      { kind: 'slot', module: 'avatar', size: { px: 200 } },
      { kind: 'slot', module: 'chat', size: { fr: 1 } },
      { kind: 'slot', module: 'content', size: { px: 760 } },
      { kind: 'slot', module: 'composer', size: { px: 96 } },
      { kind: 'slot', module: 'main-bar', size: { px: 168 } },
      { kind: 'slot', module: 'status', size: { px: 96 }, pin: 'bottom' },
    ],
  },
};

export const BUILTIN_PRESETS: readonly Preset[] = [KIOSK, TEXT, MAIN_BAR_TOP, CONTENT_FIRST, ESSENZIALE];

export const DEFAULT_PRESET_ID = KIOSK.id;

/** Every preset, built-in and from the domain, checked once: a broken one is reported, not used. */
export function collectPresets(extra: readonly unknown[] = []): { presets: Map<string, Preset>; errors: string[] } {
  const presets = new Map<string, Preset>();
  const errors: string[] = [];
  for (const candidate of [...BUILTIN_PRESETS, ...extra]) {
    const r = parsePreset(candidate);
    if (r.ok) presets.set(r.preset.id, r.preset);
    else errors.push(`preset ${(candidate as { id?: string })?.id ?? '?'}: ${r.errors.join('; ')}`);
  }
  return { presets, errors };
}
